import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { DataService } from './data.service';
import { FacilityDetailStatus } from '../models/facility-detail.model';
import { Subscription } from '../models/subscription.model';

export interface SubscriptionStatus {
  isPaid: boolean;
  facilityId: number;
  year: number;
  customerId: number;
}

@Injectable({ providedIn: 'root' })
export class SubscriptionStatusService {
  private subscriptionStatusSubject = new BehaviorSubject<SubscriptionStatus>({
    isPaid: true,
    facilityId: 0,
    year: 0,
    customerId: 0
  });

  public subscriptionStatus$: Observable<SubscriptionStatus> = this.subscriptionStatusSubject.asObservable();

  constructor(private dataService: DataService) {}

  /**
   * Check if the current facility/year combination has an active subscription
   */
  async checkSubscriptionStatus(customerId: number, facilityId: number, year: number): Promise<void> {
    try {
      // TODO: Replace with actual API call when endpoint is available
      // const subscription = await firstValueFrom(this.dataService.getSubscriptionStatus(facilityId, year));
      
      // For now, simulate the check - you'll replace this with actual API call
      const isPaid = await this.checkPaymentStatus(customerId, facilityId, year);

      this.subscriptionStatusSubject.next({
        isPaid,
        facilityId,
        year,
        customerId
      });
    } catch (error) {
      console.error('Error checking subscription status:', error);
      // Default to unpaid on error to be safe
      this.subscriptionStatusSubject.next({
        isPaid: false,
        facilityId,
        year,
        customerId
      });
    }
  }

  /**
   * Determine purchase state from facility detail status.
   * Purchased when the facility detail is active, or when an unpaid facility has a zero-dollar subscription.
   */
  private async checkPaymentStatus(customerId: number, facilityId: number, year: number): Promise<boolean> {
    const facilityDetail = await firstValueFrom(this.dataService.getFacilityDetail(facilityId, year));
    const status = facilityDetail?.status;

    console.debug('Checking payment status for FacilityDetail', facilityDetail);
    
    if (status == null) {
      return false;
    }

    console.debug('FacilityDetail status is', status);
    
    if (status !== FacilityDetailStatus.Unpaid) {
      return true;
    }

    if (!facilityDetail?.id) {
      return false;
    }

    const subscriptionInfo = await firstValueFrom(
      this.dataService.getSubscriptionInfoForFacilityDetail(facilityDetail.id)
    );
    const amount = this.getSubscriptionAmount(subscriptionInfo);

    console.debug('FacilityDetail status is Unpaid with subscription amount', amount);
    if (amount !== 0) {
      return false;
    }

    await this.markFacilityDetailAsPaid(facilityDetail.id);
    return true;
  }

  private async markFacilityDetailAsPaid(facilityDetailId: number): Promise<void> {
    await firstValueFrom(
      this.dataService.updateFacilityDetailStatus(facilityDetailId, FacilityDetailStatus.Active)
    );
  }

  private getSubscriptionAmount(subscriptionInfo: Subscription): number | null {
    if (typeof subscriptionInfo.price === 'number' && Number.isFinite(subscriptionInfo.price)) {
      return subscriptionInfo.price;
    }

    return null;
  }

  /**
   * Get current subscription status synchronously
   */
  getCurrentStatus(): SubscriptionStatus {
    return this.subscriptionStatusSubject.value;
  }

  /**
   * Mark current facility/year as paid (e.g., after successful purchase)
   */
  markAsPaid(): void {
    const current = this.subscriptionStatusSubject.value;
    this.subscriptionStatusSubject.next({
      ...current,
      isPaid: true
    });
  }
}
